<template>
  <section class="earnings-panel">
    <div class="mobile-earnings-view">
      <header class="mobile-earnings-toolbar">
        <div>
          <h1>{{ $t('收益') }}</h1>
          <p>{{ $t('收益来源、风险水平与策略表现分析') }}</p>
        </div>
        <div class="mobile-earnings-toolbar__actions">
          <select v-model="periodKey" :aria-label="$t('选择收益月份')" @change="emitPeriod">
            <option v-for="item in periodOptions" :key="item.key" :value="item.key">
              {{ item.label }}
            </option>
          </select>
          <button type="button" :aria-label="$t('导出收益报表')" @click="exportReport">
            <i class="el-icon-download" />
          </button>
        </div>
      </header>

      <section class="mobile-earnings-card mobile-earnings-summary" :aria-label="$t('收益摘要')">
        <article>
          <span>{{ $t('今日收益 (USDT)') }}</span>
          <strong :class="valueTone(earnings.todayProfit)">{{ signed(earnings.todayProfit) }}</strong>
          <small>{{ settledCaption }}</small>
        </article>
        <article>
          <span>{{ $t('本月收益 (USDT)') }}</span>
          <strong :class="valueTone(earnings.monthProfit)">{{ signed(earnings.monthProfit) }}</strong>
          <small>{{ $t('目标完成度') }} {{ monthCompletion === null ? '--' : `${formatPlain(monthCompletion, 0)}%` }}</small>
        </article>
        <article>
          <span>{{ $t('本月收益率') }}</span>
          <strong :class="valueTone(normalizedRate(earnings.monthYield))">
            {{ rateText(earnings.monthYield, true) }}
          </strong>
          <small>{{ $t('最大回撤') }} {{ drawdownText }}</small>
        </article>
        <article>
          <span>{{ $t('累计收益 (USDT)') }}</span>
          <strong :class="valueTone(earnings.profit)">{{ signed(earnings.profit) }}</strong>
          <small>{{ $t('累计收益率') }} {{ rateText(earnings.yield, true) }}</small>
        </article>
      </section>

      <section class="mobile-earnings-card mobile-trend-card">
        <div class="mobile-card-heading">
          <h2>{{ $t('收益趋势') }}</h2>
          <div class="mobile-segmented" :aria-label="$t('收益趋势时间范围')">
            <button
              v-for="item in chartRangeOptions"
              :key="item.key"
              type="button"
              :class="{ active: chartRange === item.key }"
              @click="chartRange = item.key"
            >
              {{ $t(item.label) }}
            </button>
          </div>
        </div>
        <div class="mobile-trend-metrics">
          <div>
            <span>{{ $t('累计收益 (USDT)') }}</span>
            <strong :class="valueTone(chartMetricProfit)">{{ signed(chartMetricProfit) }}</strong>
          </div>
          <div>
            <span>{{ $t('累计收益率') }}</span>
            <strong :class="valueTone(normalizedRate(chartMetricRate))">{{ rateText(chartMetricRate, true) }}</strong>
          </div>
        </div>
        <div class="mobile-trend-chart">
          <EChartLine
            v-if="chartPoints.length"
            :points="chartPoints"
            :labels="chartLabels"
            :tooltip-rates="chartTooltipRates"
            :axis-label-count="4"
            :axis-font-size="10"
            :grid-top="18"
            :grid-bottom="38"
            :split-number="3"
            :smooth="0"
            :area-opacity=".07"
            :show-axis="true"
            tooltip-mode="overview"
            :aria-label="$t('收益趋势折线图')"
          />
          <div v-else class="mobile-earnings-empty">
            {{ loading ? $t('正在加载收益趋势…') : $t('暂无收益趋势数据') }}
          </div>
        </div>
      </section>

      <section class="mobile-earnings-card mobile-weekly-card">
        <div class="mobile-card-heading mobile-weekly-card__heading">
          <h2>{{ $t('本周收益榜') }}</h2>
          <span>{{ weekRangeLabel }}</span>
        </div>
        <div v-if="visibleLeaders.length" class="mobile-weekly-list">
          <article v-for="leader in visibleLeaders" :key="`${leader.rank}-${leader.name}`" :class="{ mine: leader.mine }">
            <div>
              <i :class="`rank-${leader.rank}`">{{ leader.rank }}</i>
              <strong>{{ leader.name }}</strong>
              <img
                v-if="Number(leader.rank) === 1"
                src="@/assets/rebuild/weekly-board-crown-gold.svg"
                alt=""
                aria-hidden="true"
              >
              <em v-if="leader.mine">{{ $t('我') }}</em>
            </div>
            <strong :class="valueTone(leader.amountValue)">{{ leader.amount }}</strong>
          </article>
        </div>
        <div v-else class="mobile-earnings-empty">
          {{ loading ? $t('正在加载榜单…') : $t('暂无榜单数据') }}
        </div>
        <button type="button" class="mobile-weekly-card__more" @click="$emit('leaderboard')">
          {{ $t('查看完整榜单') }} <i class="el-icon-arrow-right" />
        </button>
      </section>

      <section class="mobile-earnings-card mobile-composition-card">
        <div class="mobile-card-heading">
          <h2>{{ $t('收益构成') }}</h2>
          <div class="mobile-segmented">
            <button type="button" :class="{ active: compositionMode === 'strategy' }" @click="compositionMode = 'strategy'">
              {{ $t('按策略') }}
            </button>
            <button type="button" :class="{ active: compositionMode === 'exchange' }" @click="compositionMode = 'exchange'">
              {{ $t('按交易所') }}
            </button>
          </div>
        </div>
        <div v-if="mobileUnifiedCompositionRows.length" class="mobile-composition-list">
          <article v-for="row in mobileUnifiedCompositionRows" :key="row.key">
            <div>
              <strong><i :style="{ background: row.color }" />{{ $t(row.name) }}</strong>
              <em :class="valueTone(row.amount)">{{ signed(row.amount) }}</em>
            </div>
            <div v-if="row.count !== null || row.rate !== null" class="mobile-composition-meta">
              <span>{{ $t('成交单数') }} {{ row.count === null ? '--' : formatPlain(row.count, 0) }}</span>
              <span>{{ $t('收益率') }} {{ row.rate === null ? '--' : formatRateValue(row.rate) }}</span>
            </div>
            <div class="mobile-composition-progress">
              <i><b :style="{ width: `${row.share}%`, background: row.color }" /></i>
              <span>{{ compositionTotal === 0 && !row.fallback ? '--' : `${formatPlain(row.share, 1)}%` }}</span>
            </div>
          </article>
        </div>
        <div v-else class="mobile-earnings-empty">
          {{ loading ? $t('正在加载收益数据…') : $t('暂无收益构成数据') }}
        </div>
        <p>{{ $t('收益构成基于本月已结算数据统计，每日 00:00 更新。') }}</p>
      </section>

      <section class="mobile-earnings-card mobile-calendar-card">
        <div class="mobile-card-heading">
          <div>
            <h2>{{ $t('收益日历') }}</h2>
            <span>{{ periodDisplayLabel }}</span>
          </div>
          <strong :class="valueTone(calendarStats.total)">{{ signed(calendarStats.total) }}</strong>
        </div>
        <div class="mobile-calendar-grid">
          <span v-for="weekday in weekdays" :key="weekday" class="mobile-calendar-weekday">{{ $t(weekday) }}</span>
          <button
            v-for="(cell, index) in calendarCells"
            :key="cell.key || `mobile-blank-${index}`"
            type="button"
            class="mobile-calendar-day"
            :class="calendarCellClass(cell)"
            :title="cellTitle(cell)"
          >
            {{ cell.day || '' }}
          </button>
        </div>
        <div class="mobile-calendar-stats">
          <article>
            <span>{{ $t('最佳单日') }}</span>
            <strong :class="valueTone(calendarStats.bestProfit)">{{ signed(calendarStats.bestProfit) }}</strong>
            <small>{{ calendarStats.bestDate || '--' }}</small>
          </article>
          <article>
            <span>{{ $t('盈利天数') }}</span>
            <strong>{{ calendarStats.settledDays ? `${calendarStats.profitableDays}/${calendarStats.settledDays}` : '--' }}</strong>
            <small>{{ $t('胜率') }} {{ calendarStats.winRate === null ? '--' : `${formatPlain(calendarStats.winRate, 0)}%` }}</small>
          </article>
          <article>
            <span>{{ $t('日均收益') }}</span>
            <strong :class="valueTone(calendarStats.average)">{{ signed(calendarStats.average) }}</strong>
            <small>USDT / {{ $t('天') }}</small>
          </article>
        </div>
      </section>

      <section class="mobile-earnings-card mobile-settlement-card">
        <h2>{{ $t('最近结算') }}</h2>
        <div v-if="mobileRecentRows.length" class="mobile-settlement-list">
          <article v-for="row in mobileRecentRows" :key="row.key">
            <div>
              <time>{{ row.date }}</time>
              <strong :title="row.title">{{ row.title }}</strong>
            </div>
            <em :class="valueTone(row.amount)">{{ signed(row.amount) }}</em>
          </article>
        </div>
        <div v-else class="mobile-earnings-empty">
          {{ loading ? $t('正在加载收益数据…') : $t('暂无最近结算数据') }}
        </div>
      </section>
    </div>
    <div class="desktop-earnings-view">
      <header class="earnings-page-head">
      <div>
        <h1>收益</h1>
        <p>收益来源、风险水平与策略表现分析</p>
      </div>
      <div class="earnings-page-head__actions">
        <select v-model="periodKey" aria-label="选择收益月份" @change="emitPeriod">
          <option
            v-for="item in periodOptions"
            :key="item.key"
            :value="item.key"
          >
            {{ item.label }}
          </option>
        </select>
        <button type="button" class="export-button" @click="exportReport">
          导出收益报表
        </button>
      </div>
    </header>

    <section class="summary-card up-card" aria-label="收益摘要">
      <article class="summary-item">
        <div class="summary-item__head">
          <span class="summary-icon summary-icon--green"><i class="el-icon-lightning" /></span>
          <span>今日收益 (USDT)</span>
          <em class="realtime"><i />实时</em>
        </div>
        <strong :class="valueTone(earnings.todayProfit)">{{ signed(earnings.todayProfit) }}</strong>
        <small>{{ settledCaption }}</small>
      </article>

      <article class="summary-item">
        <div class="summary-item__head">
          <span class="summary-icon summary-icon--blue"><i class="el-icon-date" /></span>
          <span>本月收益 (USDT)</span>
        </div>
        <strong :class="valueTone(earnings.monthProfit)">{{ signed(earnings.monthProfit) }}</strong>
        <div class="summary-progress">
          <i><b :style="{ width: `${monthCompletion || 0}%` }" /></i>
          <small>目标完成度 <b>{{ monthCompletion === null ? '--' : `${formatPlain(monthCompletion, 0)}%` }}</b></small>
        </div>
      </article>

      <article class="summary-item">
        <div class="summary-item__head">
          <span class="summary-icon summary-icon--orange"><i class="el-icon-data-analysis" /></span>
          <span>本月收益率</span>
        </div>
        <strong :class="valueTone(normalizedRate(earnings.monthYield))">
          {{ rateText(earnings.monthYield, true) }}
        </strong>
        <small>最大回撤 <b :class="valueTone(drawdownRate)">{{ drawdownText }}</b></small>
      </article>

      <article class="summary-item">
        <div class="summary-item__head">
          <span class="summary-icon summary-icon--cyan"><i class="el-icon-coin" /></span>
          <span>累计收益 (USDT)</span>
        </div>
        <strong :class="valueTone(earnings.profit)">{{ signed(earnings.profit) }}</strong>
        <small>累计收益率 <b :class="valueTone(normalizedRate(earnings.yield))">{{ rateText(earnings.yield, true) }}</b></small>
      </article>
    </section>

    <section class="earnings-main-grid">
      <article class="trend-card up-card">
        <div class="card-title-row">
          <h2>收益趋势</h2>
          <div class="segmented" aria-label="收益趋势时间范围">
            <button
              v-for="item in chartRangeOptions"
              :key="item.key"
              type="button"
              :class="{ active: chartRange === item.key }"
              @click="chartRange = item.key"
            >
              {{ item.label }}
            </button>
          </div>
        </div>

        <div class="trend-metrics">
          <div>
            <span>累计收益 (USDT)</span>
            <strong :class="valueTone(chartMetricProfit)">{{ signed(chartMetricProfit) }}</strong>
          </div>
          <div>
            <span>累计收益率</span>
            <strong :class="valueTone(normalizedRate(chartMetricRate))">{{ rateText(chartMetricRate, true) }}</strong>
          </div>
        </div>

        <div class="trend-chart">
          <EChartLine
            v-if="chartPoints.length"
            :points="chartPoints"
            :labels="chartLabels"
            :tooltip-rates="chartTooltipRates"
            :axis-label-count="7"
            :axis-font-size="11.5"
            :grid-top="23"
            :grid-bottom="53"
            :split-number="3"
            :smooth="0"
            :area-opacity=".07"
            :show-axis="true"
            tooltip-mode="overview"
            aria-label="收益趋势折线图"
          />
          <div v-else class="earnings-empty">
            {{ loading ? '正在加载收益趋势…' : '暂无收益趋势数据' }}
          </div>
        </div>
      </article>

      <article class="weekly-board up-card">
        <div class="weekly-board__head">
          <h2>本周收益榜</h2>
          <span>{{ weekRangeLabel }}</span>
        </div>

        <div class="weekly-board__list">
          <div
            v-for="leader in visibleLeaders"
            :key="`${leader.rank}-${leader.name}`"
            class="weekly-board__row"
            :class="{ mine: leader.mine }"
          >
            <div class="weekly-board__identity">
              <i :class="`rank-${leader.rank}`">{{ leader.rank }}</i>
              <strong>{{ leader.name }}</strong>
              <img
                v-if="Number(leader.rank) === 1"
                class="leader-crown"
                src="@/assets/rebuild/weekly-board-crown-gold.svg"
                alt=""
                aria-hidden="true"
              >
              <em v-if="leader.mine">我</em>
            </div>
            <div class="weekly-board__result">
              <span
                v-if="leader.movement !== null"
                :class="movementClass(leader.movement)"
              >
                {{ movementText(leader.movement) }}
              </span>
              <span class="weekly-board__amount">
                <strong :class="valueTone(leader.amountValue)">{{ leader.amount }}</strong>
                <i><b :style="{ width: `${leader.bar}%` }" /></i>
              </span>
            </div>
          </div>
          <div v-if="!visibleLeaders.length" class="earnings-empty weekly-board__empty">
            {{ loading ? '正在加载榜单…' : '暂无榜单数据' }}
          </div>
        </div>

        <button type="button" class="weekly-board__more" @click="$emit('leaderboard')">
          查看完整榜单 <i class="el-icon-arrow-right" />
        </button>
      </article>
    </section>

    <section class="composition-card up-card">
      <div class="composition-card__head">
        <h2>收益构成</h2>
        <div class="segmented">
          <button
            type="button"
            :class="{ active: compositionMode === 'strategy' }"
            @click="compositionMode = 'strategy'"
          >
            按策略
          </button>
          <button
            type="button"
            :class="{ active: compositionMode === 'exchange' }"
            @click="compositionMode = 'exchange'"
          >
            按交易所
          </button>
        </div>
      </div>
      <div class="composition-table">
        <div class="composition-table__head">
          <span>收益来源</span>
          <span>成交单数</span>
          <span>收益金额 (USDT)</span>
          <span>收益率</span>
          <span>收益占比</span>
        </div>
        <div
          v-for="row in compositionRows"
          :key="row.key"
          class="composition-table__row"
        >
          <span :data-label="$t('收益来源')" class="composition-name"><i :style="{ background: row.color }" />{{ row.name }}</span>
          <span :data-label="$t('成交单数')">{{ row.count === null ? '--' : formatPlain(row.count, 0) }}</span>
          <strong :data-label="$t('收益金额 (USDT)')" :class="valueTone(row.amount)">{{ signed(row.amount) }}</strong>
          <span :data-label="$t('收益率')"><em v-if="row.rate !== null" :class="valueTone(row.rate)">{{ formatRateValue(row.rate) }}</em><template v-else>--</template></span>
          <span :data-label="$t('收益占比')" class="composition-share">
            <i><b :style="{ width: `${row.share}%`, background: row.color }" /></i>
            <strong>{{ compositionTotal === 0 ? '--' : `${formatPlain(row.share, 1)}%` }}</strong>
          </span>
        </div>
        <div v-if="!compositionRows.length" class="earnings-empty composition-table__empty">
          暂无收益构成数据
        </div>
      </div>
      <p class="composition-card__note">
        收益构成基于本月已结算数据统计，每日 00:00 更新。
      </p>
    </section>

    <section class="calendar-card up-card">
      <div class="calendar-card__head">
        <div>
          <h2>收益日历</h2>
          <span>{{ period.year }}年{{ period.month }}月</span>
        </div>
        <div class="calendar-legend" aria-label="收益颜色图例">
          <span>少</span>
          <i v-for="level in 5" :key="level" :class="`level-${level}`" />
          <span>多</span>
        </div>
      </div>

      <div class="calendar-layout">
        <div class="calendar-grid">
          <div v-for="weekday in weekdays" :key="weekday" class="calendar-weekday">
            {{ weekday }}
          </div>
          <div
            v-for="(cell, index) in calendarCells"
            :key="cell.key || `blank-${index}`"
            class="calendar-day"
            :class="calendarCellClass(cell)"
            :title="cellTitle(cell)"
          >
            {{ cell.day || '' }}
          </div>
        </div>

        <div class="calendar-summary">
          <article class="calendar-summary__total">
            <span>本月累计已结算 (USDT)</span>
            <strong :class="valueTone(calendarStats.total)">{{ signed(calendarStats.total) }}</strong>
          </article>
          <div class="calendar-summary__grid">
            <article>
              <span>最佳单日</span>
              <strong :class="valueTone(calendarStats.bestProfit)">{{ signed(calendarStats.bestProfit) }}</strong>
              <small>{{ calendarStats.bestDate || '--' }}</small>
            </article>
            <article>
              <span>盈利天数</span>
              <strong>{{ calendarStats.settledDays ? `${calendarStats.profitableDays} / ${calendarStats.settledDays} 天` : '--' }}</strong>
              <small>胜率 {{ calendarStats.winRate === null ? '--' : `${formatPlain(calendarStats.winRate, 0)}%` }}</small>
            </article>
            <article>
              <span>日均收益</span>
              <strong :class="valueTone(calendarStats.average)">{{ signed(calendarStats.average) }}</strong>
              <small>USDT / 天</small>
            </article>
          </div>
          <p>收益日历按自然日统计已结算收益，绿色越深代表当日收益越高；悬停单元格可查看每日明细。</p>
        </div>
      </div>
    </section>
    </div>
  </section>
</template>

<script>
import EChartLine from '@/components/rebuild/EChartLine.vue'
import { languageIntlLocale } from '@/utils/locale'
import {
  cumulativePoints,
  finiteNumber,
  firstNumber,
  firstText,
  formatNumber,
  formatRate,
  formatSigned
} from '@/utils/portal-data'

const STRATEGY_LABELS = {
  astra: 'Astra AI交易',
  futures_x: 'UPlus-X 合约交易',
  hedge: '合约AI交易',
  term: '期现AI交易',
  bb: 'BB 交易',
  ub: 'UB 交易',
  grid: '现货网格交易',
  martin: '现货马丁交易',
  sj: '黄金三角'
}

const GROUP_COLORS = ['#13A8A8', '#5B4CE2', '#1F6BFF', '#E89B2C', '#20A7D8', '#3974E8', '#E44E59']
const MOBILE_DISTRIBUTION_ROWS = [
  { key: 'triangle_profit', name: '黄金三角交易', color: '#F6BD16' },
  { key: 'bb_profit', name: '币币双向AI交易', color: '#5AD8A6' },
  { key: 'ub_profit', name: 'UB交易', color: '#5B8FF9' },
  { key: 'grid_profit', name: '现货网格', color: '#FF2870' },
  { key: 'mg_profit', name: '现货马丁', color: '#FF9500' },
  { key: 'supreme_profit', name: '智能合约交易', color: '#5AC8FA' }
]

function formatSettlementDate(value) {
  const match = String(value || '').match(/\d{4}[-/](\d{1,2})[-/](\d{1,2})(?:\s+(\d{1,2}):(\d{2}))?/)
  if (!match) return '--'
  const date = `${String(Number(match[1])).padStart(2, '0')}-${String(Number(match[2])).padStart(2, '0')}`
  return match[3] && match[4]
    ? `${date} ${String(Number(match[3])).padStart(2, '0')}:${match[4]}`
    : date
}

function settlementTitle(row) {
  const direct = firstText(row, ['product_name', 'strategy_name', 'robot_name'])
  if (direct) return direct
  const coinOne = firstText(row, ['coin_one'])
  const coinTwo = firstText(row, ['coin_two'])
  if (coinOne && coinTwo) return `${coinOne}/${coinTwo}`
  return firstText(row, ['coins', 'symbol', 'coin']) || '--'
}

function rowProfit(row) {
  return firstNumber(row, ['profit', 'trade_profit', 'profit_total', 'income'])
}

function rowDate(row) {
  return firstText(row, ['profit_day', 'date', 'day', 'create_time', 'add_time'])
}

function normalizedDateKey(value) {
  const match = String(value || '').match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/)
  if (!match) return ''
  return `${match[1]}-${String(Number(match[2])).padStart(2, '0')}-${String(Number(match[3])).padStart(2, '0')}`
}

function formatMonthDay(value) {
  const key = normalizedDateKey(value)
  return key ? key.slice(5) : '--'
}

export default {
  name: 'EarningsPanel',
  components: { EChartLine },
  props: {
    robots: { type: Array, required: true },
    earnings: { type: Object, required: true },
    leaders: { type: Array, default: () => [] },
    period: { type: Object, required: true },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      periodKey: '',
      chartRange: '30d',
      compositionMode: 'strategy',
      weekdays: ['一', '二', '三', '四', '五', '六', '日'],
      chartRangeOptions: [
        { key: '7d', label: '7 天' },
        { key: '30d', label: '30 天' },
        { key: 'year', label: '今年' }
      ]
    }
  },
  computed: {
    periodOptions() {
      const options = []
      const cursor = new Date()
      for (let index = 0; index < 24; index++) {
        const year = cursor.getFullYear()
        const month = cursor.getMonth() + 1
        options.push({
          key: `${year}-${String(month).padStart(2, '0')}`,
          label: new Intl.DateTimeFormat(languageIntlLocale(), { year: 'numeric', month: 'long' }).format(new Date(year, month - 1, 1)),
          year,
          month
        })
        cursor.setMonth(cursor.getMonth() - 1)
      }
      return options
    },
    periodDisplayLabel() {
      return new Intl.DateTimeFormat(languageIntlLocale(), {
        year: 'numeric',
        month: 'long'
      }).format(new Date(Number(this.period.year), Number(this.period.month) - 1, 1))
    },
    settledCount() {
      const values = (this.earnings.monthRows || [])
        .map(row => firstNumber(row, ['profit_times', 'trade_count', 'deal_count', 'order_count']))
        .filter(value => value !== null)
      return values.length ? values.reduce((sum, value) => sum + value, 0) : null
    },
    settledCaption() {
      return this.$t(`已结算 ${this.settledCount === null ? '--' : formatNumber(this.settledCount, 0)} 笔 · 自动结算入账`)
    },
    monthCompletion() {
      const value = firstNumber(this.earnings, ['targetCompletion', 'completionRate'])
      if (value === null) return null
      return Math.max(0, Math.min(100, value <= 1 ? value * 100 : value))
    },
    rawChartRows() {
      const source = this.chartRange === 'year'
        ? (this.earnings.allRows || [])
        : (this.earnings.monthRows || [])
      const rows = source.slice().sort((left, right) => rowDate(left).localeCompare(rowDate(right)))
      if (this.chartRange === '7d') return rows.slice(-7)
      if (this.chartRange === '30d') return rows.slice(-30)
      return rows.filter(row => {
        const key = normalizedDateKey(rowDate(row))
        return !key || Number(key.slice(0, 4)) === Number(this.period.year)
      })
    },
    chartPoints() {
      return cumulativePoints(this.rawChartRows)
    },
    chartLabels() {
      return this.rawChartRows.map(row => formatMonthDay(rowDate(row)))
    },
    chartTooltipRates() {
      return this.rawChartRows.map(row =>
        firstNumber(row, ['month_yield', 'yield', 'trade_profit_yield', 'profit_yield'])
      )
    },
    chartMetricProfit() {
      return this.chartPoints.length ? this.chartPoints[this.chartPoints.length - 1] : null
    },
    chartMetricRate() {
      const values = this.chartTooltipRates.filter(value => value !== null)
      if (values.length) return values[values.length - 1]
      if (this.chartRange === '30d') return finiteNumber(this.earnings.monthYield)
      return null
    },
    drawdownRate() {
      if (this.chartPoints.length < 2) return null
      let peak = this.chartPoints[0]
      let maxDrawdown = 0
      this.chartPoints.forEach(point => {
        peak = Math.max(peak, point)
        if (peak > 0) maxDrawdown = Math.max(maxDrawdown, (peak - point) / peak)
      })
      return maxDrawdown ? -maxDrawdown : 0
    },
    drawdownText() {
      return this.drawdownRate === null ? '--' : formatRate(this.drawdownRate, { fraction: true })
    },
    weekRangeLabel() {
      const today = new Date()
      const monday = new Date(today)
      const day = today.getDay() || 7
      monday.setDate(today.getDate() - day + 1)
      const label = date => `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      return `${label(monday)} ~ ${label(today)}`
    },
    leaderRows() {
      const max = this.leaders.reduce((value, leader) => {
        const amount = finiteNumber(leader.amountValue)
        return amount === null ? value : Math.max(value, Math.abs(amount))
      }, 0)
      return this.leaders.map(leader => {
        const amountValue = finiteNumber(leader.amountValue)
        return {
          ...leader,
          movement: firstNumber(leader.raw || {}, ['rank_change', 'rankChange', 'change_rank', 'change']),
          bar: amountValue === null || !max ? 0 : Math.max(8, Math.abs(amountValue) / max * 100)
        }
      })
    },
    visibleLeaders() {
      return this.leaderRows.slice(0, 5)
    },
    mobileCompositionRows() {
      const source = this.earnings.distribution && typeof this.earnings.distribution === 'object'
        ? this.earnings.distribution
        : {}
      const rows = MOBILE_DISTRIBUTION_ROWS
        .filter(item => Object.prototype.hasOwnProperty.call(source, item.key))
        .map(item => ({
          ...item,
          amount: finiteNumber(source[item.key])
        }))
      const apiTotal = finiteNumber(source.sum_profit)
      const calculatedTotal = rows.reduce((sum, row) => {
        return sum + (row.amount === null ? 0 : Math.abs(row.amount))
      }, 0)
      const total = apiTotal !== null && apiTotal !== 0
        ? Math.abs(apiTotal)
        : calculatedTotal
      return rows.map(row => ({
        ...row,
        share: total && row.amount !== null
          ? Math.min(100, Math.abs(row.amount) / total * 100)
          : 0
      }))
    },
    mobileUnifiedCompositionRows() {
      if (this.compositionRows.length) {
        return this.compositionRows.map(row => ({ ...row, fallback: false }))
      }
      return this.mobileCompositionRows.map(row => ({
        ...row,
        count: null,
        rate: null,
        fallback: true
      }))
    },
    mobileRecentRows() {
      return (this.earnings.records || []).slice(0, 6).map((row, index) => ({
        key: firstText(row, ['uid', 'id']) || `${firstText(row, ['create_time', 'add_time'])}-${index}`,
        date: formatSettlementDate(firstText(row, ['create_time', 'add_time', 'time_ok', 'date'])),
        title: settlementTitle(row),
        amount: firstNumber(row, ['profit_true', 'profit', 'trade_profit', 'profit_total', 'income'])
      }))
    },    compositionRows() {
      const groups = new Map()
      this.robots.forEach((robot, index) => {
        const groupValue = this.compositionMode === 'exchange'
          ? robot.exchange
          : (robot.productType || robot.name)
        const key = String(groupValue || '--')
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            name: this.compositionMode === 'exchange'
              ? (robot.exchange || '--')
              : (STRATEGY_LABELS[robot.productType] || robot.name || '--'),
            color: this.compositionMode === 'exchange'
              ? GROUP_COLORS[groups.size % GROUP_COLORS.length]
              : (robot.color || GROUP_COLORS[index % GROUP_COLORS.length]),
            amount: 0,
            amountKnown: false,
            count: 0,
            countKnown: false,
            investment: 0,
            rates: []
          })
        }
        const group = groups.get(key)
        const amount = finiteNumber(robot.profitValue)
        if (amount !== null) {
          group.amount += amount
          group.amountKnown = true
        }
        const investment = finiteNumber(robot.investmentValue)
        if (investment !== null) group.investment += investment
        const rate = finiteNumber(robot.rateValue)
        if (rate !== null) group.rates.push(rate)
        const count = firstNumber(robot.raw || {}, ['profit_times', 'trade_count', 'deal_count', 'order_count'])
        if (count !== null) {
          group.count += count
          group.countKnown = true
        }
      })
      const values = Array.from(groups.values())
      const total = values.reduce((sum, group) => sum + (group.amountKnown ? Math.abs(group.amount) : 0), 0)
      return values.map(group => {
        let rate = null
        if (group.amountKnown && group.investment) {
          rate = group.amount / group.investment * 100
        } else if (group.rates.length) {
          rate = group.rates.reduce((sum, value) => sum + value, 0) / group.rates.length
        }
        return {
          key: group.key,
          name: group.name,
          color: group.color,
          amount: group.amountKnown ? group.amount : null,
          count: group.countKnown ? group.count : null,
          rate,
          share: total && group.amountKnown ? Math.abs(group.amount) / total * 100 : 0
        }
      })
    },
    compositionTotal() {
      return this.compositionRows.reduce((sum, row) => sum + (row.amount === null ? 0 : Math.abs(row.amount)), 0)
    },
    calendarProfitMap() {
      const values = {}
      ;(this.earnings.monthRows || []).forEach(row => {
        const key = normalizedDateKey(rowDate(row))
        if (!key) return
        const profit = rowProfit(row)
        if (!values[key]) values[key] = { profit: 0, known: false }
        if (profit !== null) {
          values[key].profit += profit
          values[key].known = true
        }
      })
      return values
    },
    calendarMaxProfit() {
      return Object.values(this.calendarProfitMap).reduce((max, item) => {
        return item.known && item.profit > 0 ? Math.max(max, item.profit) : max
      }, 0)
    },
    calendarCells() {
      const year = Number(this.period.year)
      const month = Number(this.period.month)
      const firstDay = new Date(year, month - 1, 1)
      const leading = (firstDay.getDay() + 6) % 7
      const days = new Date(year, month, 0).getDate()
      const today = new Date()
      const cells = Array.from({ length: leading }, (_, index) => ({ key: `blank-${index}`, blank: true }))
      for (let day = 1; day <= days; day++) {
        const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        const date = new Date(year, month - 1, day)
        const data = this.calendarProfitMap[key] || { profit: null, known: false }
        const ratio = data.known && data.profit > 0 && this.calendarMaxProfit
          ? data.profit / this.calendarMaxProfit
          : 0
        cells.push({
          key,
          day,
          profit: data.known ? data.profit : null,
          hasValue: data.known,
          future: date > new Date(today.getFullYear(), today.getMonth(), today.getDate()),
          today: year === today.getFullYear() && month === today.getMonth() + 1 && day === today.getDate(),
          level: ratio <= 0 ? 0 : Math.min(5, Math.max(1, Math.ceil(ratio * 5)))
        })
      }
      return cells
    },
    calendarStats() {
      const rows = Object.keys(this.calendarProfitMap)
        .map(key => ({ key, ...this.calendarProfitMap[key] }))
        .filter(item => item.known)
      if (!rows.length) {
        return {
          total: null,
          bestProfit: null,
          bestDate: '',
          settledDays: 0,
          profitableDays: 0,
          winRate: null,
          average: null
        }
      }
      const total = rows.reduce((sum, item) => sum + item.profit, 0)
      const best = rows.reduce((current, item) => item.profit > current.profit ? item : current, rows[0])
      const profitableDays = rows.filter(item => item.profit > 0).length
      return {
        total,
        bestProfit: best.profit,
        bestDate: `${String(Number(best.key.slice(5, 7))).padStart(2, '0')}-${String(Number(best.key.slice(8, 10))).padStart(2, '0')}`,
        settledDays: rows.length,
        profitableDays,
        winRate: rows.length ? profitableDays / rows.length * 100 : null,
        average: rows.length ? total / rows.length : null
      }
    }
  },
  watch: {
    period: {
      immediate: true,
      deep: true,
      handler(value) {
        if (!value) return
        this.periodKey = `${value.year}-${String(value.month).padStart(2, '0')}`
      }
    }
  },
  methods: {
    signed(value) {
      return formatSigned(value)
    },
    rateText(value, fraction) {
      return formatRate(value, { fraction })
    },
    normalizedRate(value) {
      const number = finiteNumber(value)
      return number === null ? null : number * 100
    },
    formatPlain(value, digits = 2) {
      return formatNumber(value, digits)
    },
    formatRateValue(value) {
      return formatRate(value)
    },
    valueTone(value) {
      const number = finiteNumber(value)
      if (number === null || number === 0) return 'is-neutral'
      return number > 0 ? 'is-positive' : 'is-negative'
    },
    movementClass(value) {
      if (value > 0) return 'movement-up'
      if (value < 0) return 'movement-down'
      return 'movement-flat'
    },
    movementText(value) {
      if (value > 0) return `▲ ${formatNumber(Math.abs(value), 0)}`
      if (value < 0) return `▼ ${formatNumber(Math.abs(value), 0)}`
      return '—'
    },
    calendarCellClass(cell) {
      if (cell.blank) return 'blank'
      return {
        future: cell.future,
        today: cell.today,
        negative: cell.hasValue && cell.profit < 0,
        empty: !cell.hasValue,
        [`level-${cell.level}`]: cell.level > 0
      }
    },
    cellTitle(cell) {
      if (!cell || cell.blank) return ''
      return `${cell.key} · 已结算收益 ${cell.profit === null ? '--' : `${formatSigned(cell.profit)} USDT`}`
    },
    emitPeriod() {
      const selected = this.periodOptions.find(item => item.key === this.periodKey)
      if (selected) this.$emit('period-change', { year: selected.year, month: selected.month })
    },
    exportReport() {
      const title = ['项目', '数值'].map(label => this.$t(label))
      const rows = [
        [this.$t('月份'), `${this.period.year}-${String(this.period.month).padStart(2, '0')}`],
        [this.$t('今日收益 (USDT)'), this.signed(this.earnings.todayProfit)],
        [this.$t('本月收益 (USDT)'), this.signed(this.earnings.monthProfit)],
        [this.$t('本月收益率'), this.rateText(this.earnings.monthYield, true)],
        [this.$t('累计收益 (USDT)'), this.signed(this.earnings.profit)],
        [this.$t('累计收益率'), this.rateText(this.earnings.yield, true)]
      ]
      const dailyRows = Object.keys(this.calendarProfitMap)
        .sort()
        .map(key => [key, this.calendarProfitMap[key].known ? formatNumber(this.calendarProfitMap[key].profit) : '--'])
      const content = [title, ...rows, [], ['日期', '已结算收益 (USDT)'].map(label => this.$t(label)), ...dailyRows]
        .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','))
        .join('\r\n')
      const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.href = url
      link.download = `UPlus-${this.$t('收益报表')}-${this.period.year}-${String(this.period.month).padStart(2, '0')}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.earnings-panel {
  color: #1b1f2a;
}

.mobile-earnings-view {
  display: none;
}

.earnings-panel .up-card {
  box-shadow: 0 1px 2px rgba(16, 24, 40, .04), 0 6px 20px rgba(16, 24, 40, .04);
  transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;
}

.earnings-panel .up-card:hover {
  border-color: #a9c4f7;
  box-shadow: 0 14px 34px rgba(16, 24, 40, .10);
  transform: translateY(-2px);
}

.earnings-page-head,
.earnings-page-head__actions,
.summary-item__head,
.card-title-row,
.weekly-board__head,
.weekly-board__identity,
.composition-card__head,
.calendar-card__head,
.calendar-card__head > div,
.calendar-legend,
.summary-progress,
.composition-share {
  display: flex;
  align-items: center;
}

.earnings-page-head {
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.earnings-page-head h1,
.earnings-page-head p,
.card-title-row h2,
.weekly-board h2,
.composition-card h2,
.calendar-card h2,
.composition-card__note,
.calendar-summary p {
  margin: 0;
}

.earnings-page-head h1 {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: .2px;
}

.earnings-page-head p {
  margin-top: 5px;
  color: #8a90a0;
  font-size: 13px;
}

.earnings-page-head__actions {
  gap: 10px;
}

.earnings-page-head select {
  padding: 9px 38px 9px 10px;
  color: #3a3f4e;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background-color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.export-button {
  padding: 10px 18px;
  color: #fff;
  border: 0;
  border-radius: 8px;
  background: #1f6bff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background .16s ease, transform .16s ease;
}

.export-button:hover {
  background: #1558db;
  transform: translateY(-1px);
}

.summary-card {
  display: grid;
  overflow: hidden;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 20px;
  border-radius: 14px;
}

.summary-item {
  min-width: 0;
  padding: 22px 26px;
}

.summary-item + .summary-item {
  border-left: 1px solid #f0f2f6;
}

.summary-item__head {
  gap: 9px;
  color: #79808f;
  font-size: 13.5px;
  font-weight: 500;
}

.summary-icon {
  display: grid;
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  place-items: center;
  border-radius: 8px;
  font-size: 14px;
}

.summary-icon--green { color: #0b9b57; background: #e9f8f0; }
.summary-icon--blue { color: #1f6bff; background: #ebf1fe; }
.summary-icon--orange { color: #b45309; background: #fbf1df; }
.summary-icon--cyan { color: #0c87be; background: #e0f4fd; }

.realtime {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 3px 9px;
  color: #0b7a47;
  border-radius: 999px;
  background: #f0faf4;
  font-size: 11px;
  font-style: normal;
  font-weight: 800;
}

.realtime i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #12b76a;
}

.summary-item > strong {
  display: block;
  margin-top: 12px;
  font-size: 30px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  letter-spacing: -.3px;
  line-height: 1.1;
}

.summary-item > small,
.summary-progress small {
  display: block;
  margin-top: 6px;
  color: #8a90a0;
  font-size: 13px;
  font-weight: 500;
}

.summary-item small b {
  font-weight: 700;
}

.summary-progress {
  gap: 9px;
  margin-top: 8px;
}

.summary-progress > i {
  width: min(130px, 100%);
  height: 6px;
  overflow: hidden;
  flex: 1 1 130px;
  max-width: 130px;
  border-radius: 999px;
  background: #eef0f5;
}

.summary-progress > i b {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #1f6bff;
}

.summary-progress small {
  margin-top: 0;
  font-size: 12.5px;
  white-space: nowrap;
}

.summary-progress small b {
  color: #1f6bff;
  font-weight: 700;
}

.is-positive { color: #0b9b57 !important; }
.is-negative { color: #d92d20 !important; }
.is-neutral { color: #1b1f2a !important; }

.earnings-main-grid {
  display: grid;
  height: 428px;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: 20px;
  align-items: stretch;
}

.trend-card,
.weekly-board,
.composition-card,
.calendar-card {
  border-radius: 14px;
}

.trend-card,
.weekly-board {
  height: 428px;
  min-height: 0;
}

.trend-card {
  min-width: 0;
  padding: 22px 22px 12px;
}

.card-title-row,
.composition-card__head,
.calendar-card__head {
  justify-content: space-between;
}

.card-title-row h2,
.weekly-board h2,
.composition-card h2,
.calendar-card h2 {
  font-size: 16px;
  font-weight: 800;
}

.segmented {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 9px;
  background: #f5f6f9;
}

.segmented button {
  padding: 6px 14px;
  color: #79808f;
  border: 0;
  border-radius: 7px;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.segmented button.active {
  color: #1f6bff;
  background: #fff;
  box-shadow: 0 1px 4px rgba(16, 24, 40, .08);
}

.trend-metrics {
  display: flex;
  gap: 36px;
  margin: 16px 0 6px;
}

.trend-metrics div {
  min-width: 116px;
}

.trend-metrics span {
  display: block;
  color: #8a90a0;
  font-size: 12.5px;
}

.trend-metrics strong {
  display: block;
  margin-top: 2px;
  font-size: 26px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
}

.trend-chart {
  height: 280px;
}

.earnings-empty {
  display: grid;
  height: 100%;
  min-height: 100px;
  place-items: center;
  color: #98a0af;
  font-size: 12.5px;
}

.weekly-board {
  display: flex;
  padding: 22px;
  flex-direction: column;
}

.weekly-board__head {
  justify-content: space-between;
  margin-bottom: 6px;
}

.weekly-board__head > span {
  color: #98a0af;
  font-size: 12px;
}

.weekly-board__list {
  min-height: 0;
  overflow: auto;
  flex: 1;
  scrollbar-width: thin;
  scrollbar-color: #cbd3df transparent;
}

.weekly-board__row {
  display: flex;
  padding: 11px 10px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 10px;
  transition: background .15s ease;
}

.weekly-board__row:hover {
  background: #f0f5fe;
}

.weekly-board__row.mine {
  background: #f3f7fe;
}

.weekly-board__identity {
  min-width: 0;
  gap: 12px;
}

.weekly-board__identity > i {
  display: grid;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  place-items: center;
  color: #5a6072;
  border-radius: 50%;
  background: #f1f3f7;
  font-size: 13px;
  font-style: normal;
  font-weight: 800;
}

.weekly-board__identity > i.rank-1 { color: #a76e11; background: #fff0c9; }
.weekly-board__identity > i.rank-3 { color: #bd681e; background: #ffeadc; }

.weekly-board__identity strong {
  overflow: hidden;
  color: #3a3f4e;
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.leader-crown {
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
}

.weekly-board__identity em {
  padding: 2px 9px;
  color: #fff;
  border-radius: 999px;
  background: #1f6bff;
  font-size: 11px;
  font-style: normal;
  font-weight: 800;
}

.weekly-board__result {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.weekly-board__result > span {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
}

.movement-up { color: #0b7a47; background: #e9f8f0; }
.movement-down { color: #d92d20; background: #fdecec; }
.movement-flat { color: #79808f; background: #f3f4f7; }

.weekly-board__amount {
  display: flex !important;
  padding: 0 !important;
  align-items: flex-end !important;
  flex-direction: column;
  gap: 5px;
  border-radius: 0 !important;
}

.weekly-board__amount > strong {
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
}

.weekly-board__amount > i {
  width: 74px;
  height: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: #eef0f5;
}

.weekly-board__amount > i b {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #1f6bff;
}

.weekly-board__empty {
  min-height: 180px;
}

.weekly-board__more {
  width: 100%;
  margin-top: auto;
  padding: 12px 0 2px;
  color: #1f6bff;
  border: 0;
  border-top: 1px solid #f3f4f8;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.composition-card {
  margin-top: 20px;
  overflow: hidden;
}

.composition-card__head {
  padding: 20px 24px 14px;
}

.composition-table__head,
.composition-table__row {
  display: grid;
  grid-template-columns: 1.5fr .9fr 1.1fr .8fr 2.4fr;
  align-items: center;
  gap: 10px;
}

.composition-table__head {
  padding: 8px 24px;
  color: #8a90a0;
  border-bottom: 1px solid #f0f2f6;
  font-size: 12.5px;
  font-weight: 600;
}

.composition-table__row {
  padding: 15px 24px;
  border-bottom: 1px solid #f6f7fa;
  color: #3a3f4e;
  font-size: 14px;
}

.composition-table__row:hover {
  background: #fafafd;
}

.composition-table__row > strong {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.composition-name {
  display: flex;
  align-items: center;
  min-width: 0;
  color: #1b1f2a;
  font-weight: 700;
}

.composition-name i {
  width: 10px;
  height: 10px;
  margin-right: 10px;
  flex: 0 0 10px;
  border-radius: 3px;
}

.composition-table__row em {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: #e8f6ee;
  font-size: 12.5px;
  font-style: normal;
  font-weight: 700;
}

.composition-table__row em.is-negative {
  background: #fdecec;
}

.composition-share {
  min-width: 0;
  gap: 12px;
}

.composition-share > i {
  height: 8px;
  overflow: hidden;
  flex: 1;
  border-radius: 999px;
  background: #eef0f5;
}

.composition-share > i b {
  display: block;
  height: 100%;
  border-radius: inherit;
}

.composition-share > strong {
  width: 48px;
  color: #3a3f4e;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.composition-table__empty {
  min-height: 120px;
}

.composition-card__note {
  padding: 14px 24px;
  color: #98a0af;
  font-size: 12.5px;
}

.calendar-card {
  margin-top: 20px;
  padding: 22px 24px;
}

.calendar-card__head > div {
  gap: 10px;
}

.calendar-card__head > div > span {
  padding: 3px 11px;
  color: #1f6bff;
  border-radius: 999px;
  background: #ebf1fe;
  font-size: 11.5px;
  font-weight: 800;
}

.calendar-legend {
  gap: 5px;
  color: #98a0af;
  font-size: 11.5px;
}

.calendar-legend i {
  width: 14px;
  height: 14px;
  border-radius: 4px;
}

.calendar-layout {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 40px;
  align-items: flex-start;
  margin-top: 16px;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 46px);
  gap: 6px;
}

.calendar-weekday {
  color: #98a0af;
  font-size: 11.5px;
  font-weight: 700;
  text-align: center;
}

.calendar-day {
  display: grid;
  width: 46px;
  height: 46px;
  place-items: center;
  color: #137845;
  border: 1.5px solid transparent;
  border-radius: 8px;
  background: #f1f3f7;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  cursor: default;
  transition: transform .12s ease;
}

.calendar-day:not(.blank):hover {
  transform: scale(1.1);
}

.calendar-day.blank {
  visibility: hidden;
}

.calendar-day.empty {
  color: #98a0af;
  background: #f1f3f8;
}

.calendar-day.future {
  color: #c2c7d0;
  background: #fafbfc;
}

.calendar-day.negative {
  color: #d92d20;
  background: #fdecec;
}

.calendar-day.today {
  border-color: #1f6bff;
}

.calendar-legend .level-1 { background: #f1f3f8; }
.calendar-legend .level-2 { background: #dff3e8; }
.calendar-legend .level-3 { background: #afe3c6; }
.calendar-legend .level-4 { background: #6fcb9c; }
.calendar-legend .level-5 { background: #2fa96f; }

.calendar-day.level-1 { background: #dff3e8; }
.calendar-day.level-2 { background: #afe3c6; }
.calendar-day.level-3 { color: #08743f; background: #8ed9b0; }
.calendar-day.level-4 { color: #fff; background: #6fcb9c; }
.calendar-day.level-5 { color: #fff; background: #2fa96f; }

.calendar-summary {
  min-width: 0;
}

.calendar-summary article {
  border: 1px solid #eef1f7;
  border-radius: 12px;
  background: #f8fafd;
}

.calendar-summary__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 18px;
}

.calendar-summary__total span {
  color: #79808f;
  font-size: 13px;
  font-weight: 600;
}

.calendar-summary article strong {
  font-variant-numeric: tabular-nums;
  font-weight: 800;
}

.calendar-summary__total strong {
  font-size: 21px;
}

.calendar-summary__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.calendar-summary__grid article {
  padding: 14px 16px;
}

.calendar-summary__grid span,
.calendar-summary__grid strong,
.calendar-summary__grid small {
  display: block;
}

.calendar-summary__grid span {
  color: #8a90a0;
  font-size: 12px;
  font-weight: 400;
}

.calendar-summary__grid strong {
  margin-top: 5px;
  color: #1b1f2a;
  font-size: 17px;
}

.calendar-summary__grid small {
  margin-top: 3px;
  color: #98a0af;
  font-size: 11.5px;
}

.calendar-summary p {
  margin-top: 12px;
  color: #98a0af;
  font-size: 12px;
  line-height: 1.7;
}

@media (max-width: 1120px) {
  .summary-card {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-item:nth-child(3) {
    border-left: 0;
    border-top: 1px solid #f0f2f6;
  }

  .summary-item:nth-child(4) {
    border-top: 1px solid #f0f2f6;
  }

  .earnings-main-grid {
    height: auto;
    grid-template-columns: 1fr;
  }

  .weekly-board {
    height: auto;
    min-height: 410px;
  }

  .trend-card {
    height: auto;
  }

  .calendar-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .desktop-earnings-view {
    display: none;
  }

  .mobile-earnings-view {
    display: grid;
    width: 100%;
    gap: 12px;
    box-sizing: border-box;
  }

  .mobile-earnings-view button,
  .mobile-earnings-view select {
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-earnings-toolbar,
  .mobile-earnings-toolbar__actions,
  .mobile-card-heading,
  .mobile-trend-metrics,
  .mobile-weekly-list article,
  .mobile-weekly-list article > div,
  .mobile-composition-list article > div:first-child,
  .mobile-composition-progress,
  .mobile-composition-meta,
  .mobile-settlement-list article,
  .mobile-settlement-list article > div {
    display: flex;
    align-items: center;
  }

  .mobile-earnings-toolbar {
    justify-content: space-between;
    gap: 10px;
    padding: 1px 2px 2px;
  }

  .mobile-earnings-toolbar > div:first-child {
    min-width: 0;
  }

  .mobile-earnings-toolbar h1,
  .mobile-earnings-toolbar p {
    margin: 0;
  }

  .mobile-earnings-toolbar h1 {
    color: #111827;
    font-size: 20px;
    line-height: 28px;
    font-weight: 800;
  }

  .mobile-earnings-toolbar p {
    max-width: 190px;
    margin-top: 2px;
    overflow: hidden;
    color: #98a0af;
    font-size: 10.5px;
    line-height: 15px;
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-earnings-toolbar__actions {
    flex: 0 0 auto;
    gap: 7px;
  }

  .mobile-earnings-toolbar__actions select,
  .mobile-earnings-toolbar__actions button {
    height: 36px;
    box-sizing: border-box;
    color: #4f5668;
    border: 1px solid #dfe5ef;
    border-radius: 10px;
    background: #fff;
  }

  .mobile-earnings-toolbar__actions select {
    max-width: 112px;
    padding: 0 26px 0 10px;
    font-size: 12px;
    font-weight: 600;
  }

  .mobile-earnings-toolbar__actions button {
    width: 36px;
    padding: 0;
    color: #1f6bff;
    font-size: 15px;
  }

  .mobile-earnings-card {
    width: 100%;
    padding: 16px;
    box-sizing: border-box;
    overflow: hidden;
    border: 1px solid #ebeef4;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 22px rgba(16, 24, 40, .055);
  }

  .mobile-earnings-summary {
    display: grid;
    padding: 0;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mobile-earnings-summary article {
    min-width: 0;
    min-height: 103px;
    padding: 16px;
    box-sizing: border-box;
  }

  .mobile-earnings-summary article:nth-child(even) {
    border-left: 1px solid #eef1f6;
  }

  .mobile-earnings-summary article:nth-child(n+3) {
    border-top: 1px solid #eef1f6;
  }

  .mobile-earnings-summary span,
  .mobile-earnings-summary strong,
  .mobile-earnings-summary small {
    display: block;
  }

  .mobile-earnings-summary span {
    overflow: hidden;
    color: #79808f;
    font-size: 11px;
    line-height: 16px;
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-earnings-summary strong {
    margin-top: 5px;
    color: #111827;
    font-size: 21px;
    line-height: 28px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }

  .mobile-earnings-summary small {
    margin-top: 4px;
    overflow: hidden;
    color: #98a0af;
    font-size: 10.5px;
    line-height: 15px;
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-earnings-card h2 {
    margin: 0;
    color: #111827;
    font-size: 15.5px;
    line-height: 22px;
    font-weight: 800;
  }

  .mobile-card-heading {
    min-width: 0;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
  }

  .mobile-card-heading > div:first-child {
    min-width: 0;
  }

  .mobile-card-heading > div:first-child > span,
  .mobile-weekly-card__heading > span {
    display: block;
    margin-top: 2px;
    color: #98a0af;
    font-size: 10.5px;
    line-height: 15px;
    font-weight: 400;
  }

  .mobile-segmented {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    padding: 3px;
    border-radius: 9px;
    background: #f2f4f8;
  }

  .mobile-segmented button {
    min-width: 43px;
    height: 28px;
    padding: 0 9px;
    color: #79808f;
    border: 0;
    border-radius: 7px;
    background: transparent;
    font-size: 11px;
    line-height: 28px;
    font-weight: 600;
  }

  .mobile-segmented button.active {
    color: #1f6bff;
    background: #fff;
    box-shadow: 0 1px 3px rgba(16, 24, 40, .08);
  }

  .mobile-trend-metrics {
    gap: 24px;
    margin-bottom: 4px;
  }

  .mobile-trend-metrics > div {
    min-width: 0;
  }

  .mobile-trend-metrics span,
  .mobile-trend-metrics strong {
    display: block;
  }

  .mobile-trend-metrics span {
    color: #98a0af;
    font-size: 10.5px;
    line-height: 16px;
  }

  .mobile-trend-metrics strong {
    margin-top: 3px;
    color: #111827;
    font-size: 17px;
    line-height: 24px;
    font-weight: 800;
  }

  .mobile-trend-chart {
    height: 190px;
    margin: 0 -6px -4px;
  }

  .mobile-trend-chart > * {
    width: 100%;
    height: 100%;
  }

  .mobile-weekly-card {
    padding-bottom: 14px;
  }

  .mobile-weekly-card__heading {
    align-items: flex-start;
  }

  .mobile-weekly-card__heading > span {
    margin-top: 3px;
    color: #1f6bff;
    font-weight: 600;
  }

  .mobile-weekly-list article {
    justify-content: space-between;
    gap: 12px;
    min-height: 45px;
    padding: 5px 7px;
    box-sizing: border-box;
    border-radius: 10px;
  }

  .mobile-weekly-list article.mine {
    background: #f1f5fd;
  }

  .mobile-weekly-list article > div {
    min-width: 0;
    flex: 1 1 auto;
    gap: 9px;
  }

  .mobile-weekly-list article > div > i {
    display: grid;
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    place-items: center;
    color: #687084;
    border-radius: 50%;
    background: #f1f3f7;
    font-size: 11px;
    font-style: normal;
    font-weight: 800;
  }

  .mobile-weekly-list article > div > i.rank-1 {
    color: #a86d00;
    background: #fff1ce;
  }

  .mobile-weekly-list article > div > i.rank-3 {
    color: #bf6217;
    background: #ffeadb;
  }

  .mobile-weekly-list article > div > strong {
    min-width: 0;
    overflow: hidden;
    color: #303646;
    font-size: 12px;
    line-height: 18px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-weekly-list img {
    width: 13px;
    height: 13px;
    flex: 0 0 13px;
  }

  .mobile-weekly-list em {
    padding: 1px 6px;
    color: #fff;
    border-radius: 8px;
    background: #1f6bff;
    font-size: 9px;
    line-height: 16px;
    font-style: normal;
    font-weight: 700;
  }

  .mobile-weekly-list article > strong {
    flex: 0 0 auto;
    color: #00a262;
    font-size: 12.5px;
    line-height: 18px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }

  .mobile-weekly-card__more {
    width: 100%;
    height: 38px;
    margin-top: 10px;
    color: #1f6bff;
    border: 0;
    border-radius: 9px;
    background: #edf3ff;
    font-size: 12px;
    font-weight: 700;
  }

  .mobile-composition-list article {
    padding: 10px 0;
    border-bottom: 1px solid #f2f4f8;
  }

  .mobile-composition-list article > div:first-child {
    justify-content: space-between;
    gap: 12px;
  }

  .mobile-composition-list strong,
  .mobile-composition-list em,
  .mobile-settlement-list strong,
  .mobile-settlement-list em {
    font-style: normal;
    font-variant-numeric: tabular-nums;
  }

  .mobile-composition-list strong {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 7px;
    overflow: hidden;
    color: #3a3f4e;
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-composition-list strong > i {
    width: 7px;
    height: 7px;
    flex: 0 0 7px;
    border-radius: 2px;
  }

  .mobile-composition-list em {
    flex: 0 0 auto;
    font-size: 13px;
    line-height: 18px;
    font-weight: 800;
  }

  .mobile-composition-meta {
    justify-content: space-between;
    gap: 8px;
    margin-top: 5px;
    color: #98a0af;
    font-size: 10px;
    line-height: 15px;
  }

  .mobile-composition-progress {
    gap: 10px;
    margin-top: 7px;
  }

  .mobile-composition-progress > i {
    flex: 1 1 auto;
    height: 6px;
    overflow: hidden;
    border-radius: 99px;
    background: #edf0f5;
  }

  .mobile-composition-progress b {
    display: block;
    height: 100%;
    border-radius: inherit;
  }

  .mobile-composition-progress > span {
    flex: 0 0 40px;
    color: #5a6072;
    font-size: 11px;
    line-height: 16px;
    font-weight: 700;
    text-align: right;
  }

  .mobile-composition-card > p {
    margin: 11px 0 0;
    color: #98a0af;
    font-size: 10.5px;
    line-height: 16px;
    font-weight: 400;
  }

  .mobile-calendar-card .mobile-card-heading > strong {
    flex: 0 0 auto;
    font-size: 17px;
    line-height: 24px;
    font-weight: 800;
  }

  .mobile-calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 6px;
  }

  .mobile-calendar-weekday {
    color: #98a0af;
    font-size: 9.5px;
    line-height: 18px;
    text-align: center;
  }

  .mobile-calendar-day {
    display: grid;
    height: 32px;
    padding: 0;
    place-items: center;
    color: #657084;
    border: 0;
    border-radius: 8px;
    background: #f7f9fc;
    font-size: 10.5px;
    font-weight: 600;
  }

  .mobile-calendar-day.blank {
    visibility: hidden;
  }

  .mobile-calendar-day.empty {
    color: #b3bac7;
    background: #f8f9fc;
  }

  .mobile-calendar-day.future {
    color: #cbd1db;
    background: #fafbfc;
  }

  .mobile-calendar-day.negative {
    color: #e34850;
    background: #fff0f1;
  }

  .mobile-calendar-day.today {
    box-shadow: 0 0 0 1px #1f6bff inset;
  }

  .mobile-calendar-day.level-1 { background: #e9f7ef; }
  .mobile-calendar-day.level-2 { background: #d4efdf; }
  .mobile-calendar-day.level-3 { color: #087a48; background: #a8dfbd; }
  .mobile-calendar-day.level-4 { color: #fff; background: #64c28c; }
  .mobile-calendar-day.level-5 { color: #fff; background: #2fa96f; }

  .mobile-calendar-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-top: 12px;
  }

  .mobile-calendar-stats article {
    min-width: 0;
    padding: 10px 8px;
    border-radius: 10px;
    background: #f7f9fc;
  }

  .mobile-calendar-stats span,
  .mobile-calendar-stats strong,
  .mobile-calendar-stats small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-calendar-stats span {
    color: #79808f;
    font-size: 9.5px;
    line-height: 14px;
  }

  .mobile-calendar-stats strong {
    margin-top: 3px;
    color: #1d2433;
    font-size: 12.5px;
    line-height: 18px;
    font-weight: 800;
  }

  .mobile-calendar-stats small {
    margin-top: 2px;
    color: #98a0af;
    font-size: 9px;
    line-height: 13px;
  }

  .mobile-settlement-card h2 {
    margin-bottom: 10px;
  }

  .mobile-settlement-list article {
    justify-content: space-between;
    gap: 12px;
    min-height: 38px;
    padding: 11px 0;
    border-bottom: 1px solid #f2f4f8;
  }

  .mobile-settlement-list article > div {
    min-width: 0;
    flex: 1 1 auto;
    gap: 11px;
  }

  .mobile-settlement-list time {
    flex: 0 0 auto;
    color: #98a0af;
    font-size: 11px;
    line-height: 18px;
    font-weight: 400;
    font-variant-numeric: tabular-nums;
  }

  .mobile-settlement-list strong {
    min-width: 0;
    overflow: hidden;
    color: #3a3f4e;
    font-size: 12.5px;
    line-height: 18px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-settlement-list em {
    flex: 0 0 auto;
    font-size: 12.5px;
    line-height: 18px;
    font-weight: 800;
  }

  .mobile-earnings-empty {
    display: grid;
    min-height: 72px;
    place-items: center;
    color: #98a0af;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
  }
}
</style>
